/**
 * MyVendors — landlord's private vendor rolodex. Add trusted plumbers,
 * electricians etc. with phone + typical rate, so maintenance work is
 * one tap away.
 */
import { useEffect, useState, useCallback } from "react";
import { PageHeader, Card, Button, Pill, Loader, Input, Textarea, Modal, EmptyState } from "@/components/ui/Primitives";
import { Wrench, Plus, Phone, Mail, Globe, Star, Trash2, DollarSign, Edit3 } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` });

const CATEGORIES = [
  "plumbing", "electrical", "hvac", "appliance-repair", "cleaning",
  "locksmith", "landscaping", "painting", "pest-control", "handyman",
  "flooring", "roofing", "general-contractor", "insurance",
  "tenant-screening", "other",
];

export default function MyVendors() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/landlord-vendors`, { headers: auth() });
      const d = await r.json();
      setItems(d.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function remove(v) {
    if (!window.confirm(`Remove ${v.name} from your vendors?`)) return;
    try {
      const r = await fetch(`${API}/api/landlord-vendors/${v.id}`, { method: "DELETE", headers: auth() });
      if (!r.ok) throw new Error("Delete failed");
      toast.success("Removed");
      load();
    } catch (err) { toast.error(String(err.message)); }
  }

  if (loading) return <Loader/>;
  return (
    <div data-testid="my-vendors-page">
      <PageHeader
        title="My vendors"
        subtitle="Your private rolodex of trusted, affordable vendors. Assign them to maintenance requests in one click — no more scrolling contacts."
      />
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setCreating(true)} data-testid="my-vendors-add"><Plus className="w-4 h-4 inline mr-1"/>Add vendor</Button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Wrench} title="No vendors saved yet" description="Add your go-to plumber, electrician, cleaner. Store their phone + rate so you can dispatch them instantly when a request comes in."/>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((v) => (
            <Card key={v.id} className={`p-4 ${v.preferred ? "ring-1 ring-emerald-400 bg-emerald-50/40" : ""}`} data-testid={`my-vendor-${v.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-slate-900 truncate flex items-center gap-1">
                    {v.name}
                    {v.preferred && <Star className="w-3.5 h-3.5 text-emerald-600 fill-emerald-500 shrink-0" data-testid={`preferred-${v.id}`}/>}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-0.5">{v.category.replace(/-/g, " ")}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing(v)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" data-testid={`my-vendor-edit-${v.id}`}><Edit3 className="w-3.5 h-3.5"/></button>
                  <button onClick={() => remove(v)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" data-testid={`my-vendor-delete-${v.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              </div>
              <div className="mt-2 space-y-1 text-xs">
                {v.phone && <a href={`tel:${v.phone}`} className="inline-flex items-center gap-1 text-slate-700 hover:text-emerald-700"><Phone className="w-3 h-3"/> {v.phone}</a>}
                {v.email && <div className="text-slate-500 flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0"/> {v.email}</div>}
                {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="text-slate-500 flex items-center gap-1 truncate hover:text-emerald-700"><Globe className="w-3 h-3 shrink-0"/> {v.website.replace(/^https?:\/\//, "")}</a>}
              </div>
              {(v.rate || v.typical_cost_cents) && (
                <div className="mt-3 rounded-lg bg-white/60 border border-slate-200 px-3 py-2 flex items-center gap-2">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-700 shrink-0"/>
                  <div className="text-xs">
                    <div className="font-bold text-slate-900">{v.rate || "—"}</div>
                    {v.typical_cost_cents ? <div className="text-slate-500">Typical: ${((v.typical_cost_cents || 0) / 100).toFixed(0)}</div> : null}
                  </div>
                </div>
              )}
              {v.notes && <p className="mt-2 text-xs text-slate-600 line-clamp-2">{v.notes}</p>}
            </Card>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <VendorForm
          initial={editing || { category: "plumbing", preferred: false, property_ids: [] }}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function VendorForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    name: initial.name || "",
    category: initial.category || "plumbing",
    phone: initial.phone || "",
    email: initial.email || "",
    website: initial.website || "",
    rate: initial.rate || "",
    typical_cost: initial.typical_cost_cents ? (initial.typical_cost_cents / 100).toString() : "",
    notes: initial.notes || "",
    preferred: !!initial.preferred,
  });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Vendor name is required.");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        website: form.website.trim() || null,
        rate: form.rate.trim() || null,
        typical_cost_cents: form.typical_cost ? Math.round(parseFloat(form.typical_cost) * 100) : null,
        notes: form.notes.trim() || null,
        preferred: form.preferred,
        property_ids: [],
      };
      const url = isEdit ? `${API}/api/landlord-vendors/${initial.id}` : `${API}/api/landlord-vendors`;
      const r = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Save failed");
      toast.success(isEdit ? "Vendor updated" : "Vendor added");
      onSaved(d);
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={true} onClose={onClose} title={isEdit ? "Edit vendor" : "Add a vendor"} testid="my-vendor-form">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Business name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bob's Cheap Fix" testid="mv-name"/>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="mv-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/-/g, " ")}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="mv-preferred-label">
              <input type="checkbox" checked={form.preferred} onChange={(e) => setForm({ ...form, preferred: e.target.checked })} data-testid="mv-preferred"/>
              <Star className="w-3.5 h-3.5 text-emerald-600"/> Preferred (top of list)
            </label>
          </div>
        </div>
        <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(416) 555-0142" testid="mv-phone"/>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="bob@example.com" testid="mv-email"/>
          <Input label="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" testid="mv-website"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Rate (free text)" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="$85/hr · flat $220" testid="mv-rate"/>
          <Input label="Typical cost (CAD)" inputMode="decimal" type="number" step="1" min="0" value={form.typical_cost} onChange={(e) => setForm({ ...form, typical_cost: e.target.value })} placeholder="120" testid="mv-typical-cost"/>
        </div>
        <Textarea label="Notes (optional)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="After-hours ok, cash preferred, works only west side" testid="mv-notes"/>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="mv-submit">{busy ? "Saving…" : isEdit ? "Save changes" : "Add vendor"}</Button>
        </div>
      </form>
    </Modal>
  );
}
